import type React from 'react';

import { Button, Image, Text } from '@change/ds-core-ui';
import { QuotationIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { useGetStaticAssetUrl } from '@change-corgi/core-react/assets';
import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import type { LandingPageSlug } from '#src/app/pages/landing/template/pageContext';

type Props = {
	slug: LandingPageSlug;
	onCtaClick: () => void;
};

const ROWS = ['one', 'two', 'three'];

export const Body: React.FC<Props> = ({ onCtaClick, slug }) => {
	const { translate } = useI18n();
	const getStaticAssetUrl = useGetStaticAssetUrl();

	return (
		<div className="container my-16">
			<div className="mx-8 grid grid-cols-1 gap-4 md:mx-4 md:grid-cols-[7fr_5fr]">
				<div>
					<Text size="x-large">
						<Translate value={`landing.${slug}.body.statistic_part_one`} />
						<Text as="span" className="text-typography-brand" size="x-large" fontWeight="bold">
							{translate(`landing.${slug}.body.statistic_part_red`)}
						</Text>
						<Translate value={`landing.${slug}.body.statistic_part_two`} />
					</Text>
				</div>
				<div>
					<Text as="p">
						<Translate value={`landing.${slug}.body.statistic_detail`} />
					</Text>
				</div>
			</div>
			{ROWS.map((row, idx) => (
				<div
					key={row}
					className={cn(
						// eslint-disable-next-line @change-ds/ds-no-arbitrary-spacing-classname
						'mt-[85px] grid grid-cols-1 gap-8 md:mt-[115px]',
						idx % 2 ? 'md:grid-cols-[7fr_5fr]' : 'md:grid-cols-[5fr_7fr]',
					)}
				>
					<div className={cn('text-center', idx % 2 ? 'md:order-1' : 'md:order-0')}>
						<Image
							className="mx-auto aspect-auto max-w-[80%] shadow-[20px_20px_0px_0px_#fff9df] md:max-w-[355px]"
							alt=""
							src={getStaticAssetUrl(translate(`landing.${slug}.assets.image_${row}`))}
						/>
					</div>
					<div>
						<Text as="p" size="large" fontWeight="bold" className="md:text-size-body-xl mx-4 mb-4">
							<Translate value={`landing.${slug}.body.image_${row}.caption`} />
						</Text>
						<div className="mx-4 flex">
							<div className="mr-4">
								<QuotationIcon size={28} className="opacity-40" />
							</div>
							<Text as="p" size="large" className="flex-1">
								<Translate value={`landing.${slug}.body.image_${row}.quote`} />
							</Text>
						</div>
					</div>
				</div>
			))}
			{/* eslint-disable-next-line @change-ds/ds-no-arbitrary-spacing-classname */}
			<div className="rounded-medium bg-gradient-background-dark-3 mx-4 mt-20 p-6 md:mx-10 md:mt-[100px]">
				<div className="flex flex-col items-center justify-center md:flex-row md:justify-between">
					<Text
						as="p"
						size="large"
						fontWeight="bold"
						className="text-typography-inverse-strong-fixed pb-4 text-center md:pb-0"
					>
						<Translate value={`landing.${slug}.body.body_cta.ask`} />
					</Text>
					<Button
						as="a"
						href={`/start-a-petition/intro?source_location=${slug}_landing_page`}
						onClick={onCtaClick}
						variant="primary"
						className="w-full md:w-[211px]"
					>
						<Translate value={`landing.${slug}.body.body_cta.button`} />
					</Button>
				</div>
			</div>
		</div>
	);
};
